import React, { useEffect } from "react";
import styles from "./Footer.style";
import Section from "../Section/Section";
import { Col, Image, Row } from "react-bootstrap";
import Logo from "../../assets/images/logo.png";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  EXTERNAL_LINKS,
  STORAGE_CONSTANTS,
} from "../../shared/Constants/Constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faApple,
  faFacebookF,
  faGooglePlay,
  faInstagram,
  faLinkedinIn,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";

const SOCIAL_LINKS: { label: string; href: string; icon: IconDefinition }[] = [
  { label: "Facebook", href: EXTERNAL_LINKS.FACEBOOK, icon: faFacebookF },
  { label: "Instagram", href: EXTERNAL_LINKS.INSTAGRAM, icon: faInstagram },
  { label: "LinkedIn", href: EXTERNAL_LINKS.LINKEDIN, icon: faLinkedinIn },
  { label: "TikTok", href: EXTERNAL_LINKS.TIKTOK, icon: faTiktok },
];

interface StoreBadgeProps {
  href: string;
  icon: IconDefinition;
  caption: string;
  store: string;
}

/** Black app store badge. Rendered without a link until `href` is set */
const StoreBadge: React.FC<StoreBadgeProps> = ({
  href,
  icon,
  caption,
  store,
}) => {
  const content = (
    <>
      <FontAwesomeIcon icon={icon} className="flex-shrink-0" style={{ fontSize: 16 }} />
      <span className="d-flex flex-column lh-1 text-start">
        <span className="text-uppercase" style={{ fontSize: 7 }}>
          {caption}
        </span>
        <span className="fw-semibold text-nowrap" style={{ fontSize: 12 }}>
          {store}
        </span>
      </span>
    </>
  );
  const className =
    "d-inline-flex align-items-center gap-2 bg-black text-white rounded-2 text-decoration-none";

  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      style={styles.badge}
    >
      {content}
    </a>
  ) : (
    <span className={className} style={styles.badge}>
      {content}
    </span>
  );
};

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const savedLanguage = localStorage.getItem(
      STORAGE_CONSTANTS.CURRENT_SELECTED_LANGUAGE
    );
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, []);

  const quickLinks = [
    { to: "/about-us", label: t("footer.aboutUs") },
    { to: "/faqs", label: t("footer.faq's") },
    { to: "/terms-conditions", label: t("footer.terms") },
    { to: "/privacy-policy", label: t("footer.privacy") },
    { to: "/contact-us", label: t("footer.contactUs") },
  ];

  return (
    <Section className="footer">
      <div className="position-relative overflow-hidden" style={styles.band}>
        {/* Decorative quarter shape at the band's bottom-left */}
        <div
          className="position-absolute"
          style={{ ...styles.deco, bottom: 0, left: 0, borderRadius: "0 9999px 0 0" }}
        />

        <div className="position-relative overflow-hidden" style={styles.card}>
          {/* Decorative quarter shape at the card's top-right */}
          <div
            className="position-absolute"
            style={{ ...styles.deco, top: -4, right: 0, borderRadius: "0 0 0 9999px" }}
          />

          <Row className="g-4 g-lg-0">
            <Col lg>
              <Image src={Logo} className="d-block" style={styles.logo} />
              <p className="mt-2 mb-3" style={styles.tagline}>
                Welcome to Sajilo Life, where our mission is clear: makes life
                easier.
              </p>
              <div className="d-flex gap-2">
                <StoreBadge
                  href={EXTERNAL_LINKS.PLAY_STORE}
                  icon={faGooglePlay}
                  caption="Get it on"
                  store="Google Play"
                />
                <StoreBadge
                  href={EXTERNAL_LINKS.APP_STORE}
                  icon={faApple}
                  caption="Available on the"
                  store="App Store"
                />
              </div>
            </Col>

            <Col lg="auto" className="d-none d-lg-flex">
              <div className="vr" style={styles.divider} />
            </Col>

            <Col lg="auto" className="mx-lg-5" style={{ width: 192 }}>
              <h6 style={styles.heading}>{t("footer.quickLinks")}</h6>
              <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                {quickLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="d-block text-decoration-none"
                      style={styles.link}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Col>

            <Col lg="auto" className="d-none d-lg-flex">
              <div className="vr" style={styles.divider} />
            </Col>

            <Col lg="auto" className="ms-lg-5" style={{ width: 380, maxWidth: "100%" }}>
              <h6 style={{ ...styles.heading, marginBottom: 39 }}>
                {t("footer.connectWithUs")}
              </h6>
              <div className="d-flex gap-3">
                {SOCIAL_LINKS.map(({ label, href, icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="rounded-circle d-inline-flex flex-shrink-0 align-items-center justify-content-center text-decoration-none"
                    style={styles.socialIcon}
                  >
                    <FontAwesomeIcon icon={icon} style={{ fontSize: 26 }} />
                  </a>
                ))}
              </div>
              <div className="mt-5 ms-2" style={styles.hr} />
              <p className="text-center mt-4 mb-0" style={styles.copyright}>
                © {new Date().getFullYear()} Sajilo Life. All rights reserved.
              </p>
            </Col>
          </Row>
        </div>
      </div>
    </Section>
  );
};

export default Footer;
